import { inject } from '@angular/core';
import { Router, CanActivateFn, UrlTree } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { LoggerService } from '../../logging/services/logger.service';

export type Role = 'Admin' | 'Brand' | 'Creator' | 'SuperAdmin' | 'Guest';

// Typed as possibly-undefined so the `?? DEFAULT_GUEST_REDIRECT` fallback below is
// type-checked. With a plain Record<string, string>, indexing yields `string` even for a
// missing key (e.g. 'Guest'), so a miss silently produced `undefined` at runtime and the
// user was navigated to an unresolvable path.
const ROLE_DASHBOARDS: Record<string, string | undefined> = {
  Brand: '/brand/discover',
  Creator: '/creator/listings',
  Admin: '/admin/dashboard',
  SuperAdmin: '/admin/dashboard',
};

const DEFAULT_GUEST_REDIRECT = '/';
const DEFAULT_AUTH_REDIRECT = '/auth/login';

export const roleGuard = (allowedRoles: Role[]): CanActivateFn => {
  return async (): Promise<boolean | UrlTree> => {
    const authService = inject(AuthService);
    const router = inject(Router);
    const logger = inject(LoggerService);

    if (allowedRoles.length === 0) {
      logger.error(
        'Configuration error: allowedRoles cannot be empty. Use authGuard for no restrictions.',
        'RoleGuard',
      );
      return false;
    }

    // The access token lives in memory only, so on a hard refresh the session is
    // restored asynchronously. Reading auth state synchronously here would evaluate
    // while isLoading is still true and bounce an authenticated user to /auth/login,
    // losing their deep link. waitForSession resolves once the restore settles (and has
    // its own timeout, so it cannot hang navigation).
    await authService.waitForSession();

    const user = authService.user();
    const isAuthenticated = authService.isAuthenticated();

    if (allowedRoles.includes('Guest')) {
      if (!user) {
        return true;
      }
      return router.createUrlTree([ROLE_DASHBOARDS[user.role] ?? DEFAULT_GUEST_REDIRECT]);
    }

    if (!isAuthenticated || !user) {
      return router.createUrlTree([DEFAULT_AUTH_REDIRECT]);
    }

    if (allowedRoles.includes(user.role as Role)) {
      return true;
    }

    // Returning a UrlTree lets the router perform the redirect as part of resolving this
    // navigation. The previous `router.navigate(...) + return false` raced the router's
    // own cancellation of the in-flight navigation.
    return router.createUrlTree([ROLE_DASHBOARDS[user.role] ?? DEFAULT_GUEST_REDIRECT]);
  };
};

export const authGuard: CanActivateFn = async (): Promise<boolean | UrlTree> => {
  const authService = inject(AuthService);
  const router = inject(Router);

  await authService.waitForSession();

  if (authService.isAuthenticated()) {
    return true;
  }

  return router.createUrlTree([DEFAULT_AUTH_REDIRECT]);
};

export const guestGuard: CanActivateFn = roleGuard(['Guest']);

export const adminGuard: CanActivateFn = roleGuard(['Admin', 'SuperAdmin']);
export const brandGuard: CanActivateFn = roleGuard(['Brand']);
export const creatorGuard: CanActivateFn = roleGuard(['Creator']);
export const superAdminGuard: CanActivateFn = roleGuard(['SuperAdmin']);
